import { dateFormatter } from "@/components/DateFormat/DateFormat";
import Table from "@/components/Table/Table"
import { useDebounce } from "@/hooks/debounce";
import type { RootState } from "@/store/rootReducer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, TextField, Tooltip } from "@mui/material";
import FilterComponent from "@/pages/FilterComponent/FilterComponent";
import AppliedFilter from "@/pages/FilterComponent/AppliedFilter";
import type { AppDispatch } from "@/store/store";
import { getManagers, getMyTeamGCDetail } from "@/store/slices/teamSlice";
import { ButtonLoader } from "@/components/Loader/ButtonLoader";
import { getAttendanceGeoFenceReport, getAttendanceLogsReport } from "@/store/slices/AttendenaceLogsReportSlice";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import type { showFilters } from "@/pages/FilterComponent/FilterInterface";


const AttendeanceLogs = () => {

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [searchText, setSearchText] = useState("");
    const debouncedSearch = useDebounce((searchText || "").trim());
    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
    const [filterData, setFilterData] = useState<any>({
        userIds: null,
        managerIds: null,
        fromDate: null,
        toDate: null,
    });

    const { attendeanceGeofenceReport, loading } = useSelector((state: RootState) => state.attendenaceLogsReport);
    const { teams, managers, inActiveUser } = useSelector((state: any) => state.team);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const onNavigate = () => navigate("/attendance");
    const columns = useMemo(() => [
        {
            name: "Name",
            selector: (row: any) => row?.Name ?? "-",
        },
        {
            name: "Emp No",
            selector: (row: any) => row?.EmployeeNumber ?? "-",
        },
        {
            name: "Manager",
            selector: (row: any) => row?.Manager ?? "-",
        },
        {
            name: "Geofence",
            selector: (row: any) => row?.GeoFenceName ?? "-",
        },
        {
            name: "Created Date",
            selector: (row: any) => (
                <span>
                    {row?.CreatedDate
                        ? dateFormatter(row.CreatedDate, "dd MMM yyyy hh:mm a")
                        : "-"}
                </span>
            ),
        },
        {
            name: "Exit Time",
            selector: (row: any) => (
                <span>
                    {row?.ExitTime
                        ? dateFormatter(row.ExitTime, "dd MMM yyyy hh:mm a")
                        : "-"}
                </span>
            ),
        },
        {
            name: "Entry Time",
            selector: (row: any) => (
                <span>
                    {row?.EnterTime
                        ? dateFormatter(row.EnterTime, "dd MMM yyyy hh:mm a")
                        : "-"}
                </span>
            ),
        },
        {
            name: "Entry LatLong",
            selector: (row: any) => <span>{row?.EnterLatitude ? `${row.EnterLatitude} & ${row.EnterLongitude}` : "-"}</span>,
        },
        {
            name: "Exit LatLong",
            selector: (row: any) => <span>{row?.ExitLatitude ? `${row.ExitLatitude} & ${row.ExitLongitude}` : "-"}</span>,
        },
    ], []);


    const queryParams = useMemo(() => ({
        from: (page - 1) * rowsPerPage + 1,
        to: page * rowsPerPage,
        designationIds: "",
        searchText: debouncedSearch || "",
        userIds: filterData?.userIds?.length ? filterData.userIds.map((u: any) => u.UserId).join(',') : "",
        managerIds: filterData?.managerIds?.length ? filterData.managerIds.map((m: any) => m.UserId).join(',') : "",
        fromDate: filterData?.fromDate ? dayjs(filterData.fromDate).format("MM-DD-YYYY") : null,
        toDate: filterData?.toDate ? dayjs(filterData.toDate).format("MM-DD-YYYY") : null,
    }), [
        page,
        rowsPerPage,
        debouncedSearch,
        filterData?.userIds,
        filterData?.managerIds,
        filterData?.fromDate,
        filterData?.toDate,
    ]);

    const handleExport = useCallback(() => {
        const exportParams = { ...queryParams, to: -1, from: 1 };
        dispatch(getAttendanceLogsReport(exportParams));
    }, [dispatch, queryParams]);

    const applyFilter = useCallback((data: any) => {
        setFilterData(data);
        setPage(1);
        setFilterAnchor(null);
    }, []);

    const clearFilter = useCallback((data: any) => {
        setFilterData(data);
        setPage(1);
    }, []);

    useEffect(() => {
        dispatch(getAttendanceGeoFenceReport(queryParams));
    }, [dispatch, queryParams]);

    useEffect(() => {
        dispatch(getMyTeamGCDetail({ from: 1, to: -1, text: "", Status: "all" }));
        dispatch(getManagers());
    }, [filterAnchor, filterData])

    const handleFilterClick = (e: React.MouseEvent<HTMLElement>) => {
        setFilterAnchor((prev) =>
            prev ? null : e.currentTarget
        );
    };

    const showFilters: showFilters = {
        date: { show: true, type: "date" },
        manager: { show: true, mode: "multi" },
        user: { show: true, mode: "multi" },
    };

    return (
        <>
            <div className="mb-[20px]">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-solid border-gray-200 mb-4 pb-4 pt-4">
                    <div>
                        <Button onClick={onNavigate} sx={{ minWidth: "40px" }}>
                            <img
                                src="/assets/svg/Angle-double-left.svg"
                                style={{
                                    width: "20px",
                                    height: "20px",
                                    filter:
                                        "invert(43%) sepia(71%) saturate(7492%) hue-rotate(230deg) brightness(99%) contrast(101%)",
                                }}
                                alt="Back"
                            />
                        </Button>

                        {/* Filter Button */}
                        <Tooltip title="Filter">
                            <Button variant="contained" onClick={handleFilterClick}>Filter</Button>
                        </Tooltip>

                        <FilterComponent
                            anchorEl={filterAnchor}
                            onClose={() => setFilterAnchor(null)}
                            onApply={applyFilter}
                            appliedFilter={filterData}
                            filterName="Attendance GeoFence Logs"
                            width="500px"
                            showFilters={showFilters}
                            list={{
                                managerList: (managers || []).map((m: any) => ({ ...m, UserId: m.Id })),
                                userList: teams?.Members || [],
                            }}
                        />
                    </div>

                    <div className="flex items-center gap-4">
                        <TextField
                            placeholder="Search"
                            variant="standard"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="w-90"
                        />
                        {/* Export */}
                        <ButtonLoader
                            variant="contained"
                            onClick={handleExport}
                            loading={loading.exportGroup}
                        >
                            Export
                        </ButtonLoader>
                    </div>
                </div>

                {/* Show Applied Filter */}
                <AppliedFilter filterData={filterData} onClearFilter={clearFilter} showFilters={showFilters} />
            </div>
            <Table
                columns={columns}
                data={attendeanceGeofenceReport?.Items || []}
                paginationProps={{
                    isPagination: true,
                    totalCount: attendeanceGeofenceReport?.TotalRecords || 0,
                    pageSelected: page,
                    setPageSelected: setPage,
                    rowsPerPageValue: rowsPerPage,
                    setRowsPerPageValue: setRowsPerPage,
                }}
                loading={loading.attendanceGeoFenceReport}
                paginationLabel={attendeanceGeofenceReport?.TotalRecords > 0 ? "logs" : "log"}
            />
        </>
    )
}

export default AttendeanceLogs
